import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes,} from "@softvision/webpdf-wsclient-9-typescript";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import ViewDialogFields from "../../field/document/ViewDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import DropdownNumber from "../../../../shared/components/DropdownNumber";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	BoxSelection,
	Duplex,
	Functions,
	Information,
	InitialView,
	Magnification,
	MetadataDuplex,
	MetadataInitialView,
	MetadataMagnification,
	MetadataNonFullScreenPageMode,
	MetadataPageLayout,
	MetadataPrintScale,
	MetadataPrintTraySelection,
	MetadataTextDirection,
	NonFullScreenPageMode,
	PageBox,
	PageLayout,
	PrintScale,
	PrintTraySelection,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	TextDirection,
	ToolboxOptionsOptions,
	ViewerPreferences
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import {TabPanel, TabView} from "primereact/tabview";
import {InputSwitch} from "primereact/inputswitch";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import PageRange from "../../../../shared/components/PageRange";
import DocumentSettings from "../../../document/DocumentSettings";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface ViewDialogStatus extends PortalDialogState<ToolboxOptionsOptions> {
	pages: number;
	currentPage: string;
	panel: number;
}

export class ViewDialogDefinition extends PortalDialog<ToolboxOptionsOptions,
	PortalDialogProperties<ToolboxOptionsOptions>, ViewDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			panel: 0,
			fieldErrors: new Map<ViewDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.changeDocumentView, Functions.getChangeDocumentViewDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.VIEW, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	private renderGeneralTab(parameter: ToolboxOptionsOptions): ReactNode {
		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.PAGE_MODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-page-mode">
							<Translate {...this.props} data="view.display.page.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-page-mode"
									  value={FunctionHelper.applyValue(
										  parameter.pageLayout, ToolboxOptionsOptions.getPageLayoutDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.display.page.single", {ns: "dialog"}
											  ),
											  value: PageLayout.SinglePage
										  },
										  {
											  label: Translate.value(
												  "view.display.page.single_continuous", {ns: "dialog"}
											  ),
											  value: PageLayout.SinglePageContinous
										  },
										  {
											  label: Translate.value(
												  "view.display.page.double", {ns: "dialog"}
											  ),
											  value: PageLayout.TwoPages
										  },
										  {
											  label: Translate.value(
												  "view.display.page.double_right", {ns: "dialog"}
											  ),
											  value: PageLayout.TwoPagesRight
										  },
										  {
											  label: Translate.value(
												  "view.display.page.double_continuous", {ns: "dialog"}
											  ),
											  value: PageLayout.TwoPagesContinous
										  },
										  {
											  label: Translate.value(
												  "view.display.page.double_right_continuous",
												  {ns: "dialog"}
											  ),
											  value: PageLayout.TwoPagesContinousRight
										  }
									  ]}
									  onChange={(e) => this.onChange(ViewDialogFields.PAGE_MODE, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.ZOOM_MODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-zoom-mode">
							<Translate {...this.props} data="view.display.zoom.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-zoom-mode"
									  value={FunctionHelper.applyValue(
										  parameter.magnification, ToolboxOptionsOptions.getMagnificationDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.display.zoom.none", {ns: "dialog"}
											  ),
											  value: Magnification.None
										  },
										  {
											  label: Translate.value(
												  "view.display.zoom.width", {ns: "dialog"}
											  ),
											  value: Magnification.FitWidth
										  },
										  {
											  label: Translate.value(
												  "view.display.zoom.visible", {ns: "dialog"}
											  ),
											  value: Magnification.FitVisible
										  },
										  {
											  label: Translate.value(
												  "view.display.zoom.height", {ns: "dialog"}
											  ),
											  value: Magnification.FitHeight
										  },
										  {
											  label: Translate.value(
												  "view.display.zoom.page", {ns: "dialog"}
											  ),
											  value: Magnification.FitPage
										  },
										  {
											  label: Translate.value(
												  "view.display.zoom.actual", {ns: "dialog"}
											  ),
											  value: Magnification.FitActual
										  },
										  {
											  label: Translate.value(
												  "view.display.zoom.value", {ns: "dialog"}
											  ),
											  value: Magnification.Zoom
										  }
									  ]}
									  onChange={(e) => this.onChange(ViewDialogFields.ZOOM_MODE, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						parameter.magnification !== Magnification.Zoom ||
						!this.state.fields?.contains(ViewDialogFields.ZOOM)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-zoom">
							<Translate {...this.props} data="view.display.zoom.value" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-zoom" showButtons={true}
											value={FunctionHelper.applyValue(
												parameter.magnificationZoom,
												ToolboxOptionsOptions.getMagnificationZoomDefault()
											)}
											onChange={(e) => this.onChange(ViewDialogFields.ZOOM, e.value)}
											appendTo={document.getElementById("root")} suffix=" %"
											min={ToolboxOptionsOptions.getMagnificationZoomMin()}
											max={ToolboxOptionsOptions.getMagnificationZoomMax()}
											options={[
												{label: "10", value: 10},
												{label: "25", value: 25},
												{label: "50", value: 50},
												{label: "75", value: 75},
												{label: "100", value: 100},
												{label: "125", value: 125},
												{label: "150", value: 150},
												{label: "200", value: 200},
												{label: "400", value: 400},
												{label: "800", value: 800},
												{label: "1600", value: 1600},
												{label: "2400", value: 2400},
												{label: "3200", value: 3200},
												{label: "6400", value: 6400}
											]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.OPEN_MODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-open-mode">
							<Translate {...this.props} data="view.open.mode.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-open-mode"
									  value={FunctionHelper.applyValue(
										  parameter.initialView, ToolboxOptionsOptions.getInitialViewDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.open.mode.none", {ns: "dialog"}
											  ),
											  value: InitialView.None
										  },
										  {
											  label: Translate.value(
												  "view.open.mode.outline", {ns: "dialog"}
											  ),
											  value: InitialView.Outlines
										  },
										  {
											  label: Translate.value(
												  "view.open.mode.thumbnail", {ns: "dialog"}
											  ),
											  value: InitialView.Thumbnails
										  },
										  {
											  label: Translate.value(
												  "view.open.mode.layers", {ns: "dialog"}
											  ),
											  value: InitialView.Layers
										  },
										  {
											  label: Translate.value(
												  "view.open.mode.attachments", {ns: "dialog"}
											  ),
											  value: InitialView.Attachments
										  },
										  {
											  label: Translate.value(
												  "view.open.mode.fullscreen", {ns: "dialog"}
											  ),
											  value: InitialView.Fullscreen
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.OPEN_MODE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.START_PAGE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-start-page">
							<Translate {...this.props} data="view.open.start_page" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputNumber inputId="control-start-page" showButtons={true} step={1} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter.initialPage, ToolboxOptionsOptions.getInitialPageDefault()
										 )}
										 min={ToolboxOptionsOptions.getInitialPageMin()} max={this.state.pages}
										 onChange={(e) => this.onChange(
											 ViewDialogFields.START_PAGE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.TEXT_DIRECTION)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-text-direction">
							<Translate {...this.props} data="view.text.direction.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-text-direction"
									  value={FunctionHelper.applyValue(
										  parameter.viewerPreferences?.textDirection,
										  ViewerPreferences.getTextDirectionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.text.direction.ltr", {ns: "dialog"}
											  ),
											  value: TextDirection.LeftToRight
										  },
										  {
											  label: Translate.value(
												  "view.text.direction.rtl", {ns: "dialog"}
											  ),
											  value: TextDirection.RightToLeft
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.TEXT_DIRECTION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.FIT_WINDOW)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-fit-window">
							<Translate {...this.props} data="view.window.fit.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-fit-window"
										 checked={FunctionHelper.applyValue(
											 parameter.viewerPreferences?.fitWindow,
											 ViewerPreferences.getFitWindowDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ViewDialogFields.FIT_WINDOW, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-fit-window">
								<Translate {...this.props} data="view.window.fit.value" namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.CENTER_WINDOW)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-center-window">
							<Translate {...this.props} data="view.window.center.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-center-window"
										 checked={FunctionHelper.applyValue(
											 parameter.viewerPreferences?.centerWindow,
											 ViewerPreferences.getCenterWindowDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ViewDialogFields.CENTER_WINDOW, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-center-window">
								<Translate {...this.props} data="view.window.center.value" namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.TITLE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-title">
							<Translate {...this.props} data="view.window.title.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-title"
										 checked={FunctionHelper.applyValue(
											 parameter.viewerPreferences?.displayDocTitle,
											 ViewerPreferences.getDisplayDocTitleDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ViewDialogFields.TITLE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-title">
								<Translate {...this.props} data="view.window.title.value" namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.HIDE_MENUBAR)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-hide-menubar">
							<Translate {...this.props} data="view.ui.menubar.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-hide-menubar"
										 checked={FunctionHelper.applyValue(
											 parameter.viewerPreferences?.hideMenuBar,
											 ViewerPreferences.getHideMenuBarDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ViewDialogFields.HIDE_MENUBAR, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-hide-menubar">
								<Translate {...this.props} data="view.ui.menubar.value" namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.HIDE_TOOLBAR)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-hide-toolbar">
							<Translate {...this.props} data="view.ui.toolbar.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-hide-toolbar"
										 checked={FunctionHelper.applyValue(
											 parameter.viewerPreferences?.hideToolbar,
											 ViewerPreferences.getHideToolbarDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ViewDialogFields.HIDE_TOOLBAR, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-hide-toolbar">
								<Translate {...this.props} data="view.ui.toolbar.value" namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.HIDE_WINDOW_UI)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-hide-window-ui">
							<Translate {...this.props} data="view.ui.window.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-hide-window-ui"
										 checked={FunctionHelper.applyValue(
											 parameter.viewerPreferences?.hideWindowUI,
											 ViewerPreferences.getHideWindowUIDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ViewDialogFields.HIDE_WINDOW_UI, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-hide-window-ui">
								<Translate {...this.props} data="view.ui.window.value" namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderPrintTab(parameter: ToolboxOptionsOptions): ReactNode {
		let errors: Map<ViewDialogFields, string> = this.state.fieldErrors;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.PRINT_SCALE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-print-scale">
							<Translate {...this.props} data="view.print.scale.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-print-scale"
									  value={FunctionHelper.applyValue(
										  parameter.viewerPreferences?.printScaling,
										  ViewerPreferences.getPrintScalingDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.print.scale.default", {ns: "dialog"}
											  ),
											  value: PrintScale.ReaderDefault
										  },
										  {
											  label: Translate.value(
												  "view.print.scale.none", {ns: "dialog"}
											  ),
											  value: PrintScale.NoPrintScaling
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.PRINT_SCALE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.PRINT_DUPLEX)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-print-duplex">
							<Translate {...this.props} data="view.print.duplex.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-print-duplex"
									  value={FunctionHelper.applyValue(
										  parameter.viewerPreferences?.duplex, ViewerPreferences.getDuplexDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.print.duplex.default", {ns: "dialog"}
											  ),
											  value: Duplex.None
										  },
										  {
											  label: Translate.value(
												  "view.print.duplex.simplex", {ns: "dialog"}
											  ),
											  value: Duplex.Simplex
										  },
										  {
											  label: Translate.value(
												  "view.print.duplex.short", {ns: "dialog"}
											  ),
											  value: Duplex.DuplexFlipShortEdge
										  },
										  {
											  label: Translate.value(
												  "view.print.duplex.long", {ns: "dialog"}
											  ),
											  value: Duplex.DuplexFlipLongEdge
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.PRINT_DUPLEX, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.PRINT_TRAY)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-print-tray">
							<Translate {...this.props} data="view.print.tray.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-print-tray"
									  value={FunctionHelper.applyValue(
										  parameter.viewerPreferences?.printTraySelection,
										  ViewerPreferences.getPrintTraySelectionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.print.tray.default", {ns: "dialog"}
											  ),
											  value: PrintTraySelection.ReaderDefault
										  },
										  {
											  label: Translate.value(
												  "view.print.tray.size", {ns: "dialog"}
											  ),
											  value: PrintTraySelection.ByPDFSize
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.PRINT_TRAY, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.PRINT_PAGERANGE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-print-pages">
							<Translate {...this.props} data="view.print.pages" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<PageRange inputId="control-print-pages"
									   className={errors.has(ViewDialogFields.PRINT_PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   parameter.viewerPreferences?.printPageRange,
										   ViewerPreferences.getPrintPageRangeDefault()
									   )}
									   onChange={(e) => this.onChange(
										   ViewDialogFields.PRINT_PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={Translate.value(
										   "inputs.pagerange.of", {pages: this.state.pages}
									   )}
									   tooltip={errors.has(ViewDialogFields.PRINT_PAGERANGE) ? Translate.value(
										   errors.get(ViewDialogFields.PRINT_PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(ViewDialogFields.PRINT_PAGERANGE) ? Translate.value(
								errors.get(ViewDialogFields.PRINT_PAGERANGE)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.elements.pagerange"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.PRINT_COPIES)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-print-copies">
							<Translate {...this.props} data="view.print.copies" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputNumber inputId="control-print-copies" showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter.viewerPreferences?.numCopies,
											 ViewerPreferences.getNumCopiesDefault()
										 )}
										 min={ViewerPreferences.getNumCopiesMin()}
										 onChange={(e) => this.onChange(
											 ViewDialogFields.PRINT_COPIES, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderExtendedTab(parameter: ToolboxOptionsOptions): ReactNode {
		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.VIEW_AREA)
					}>
						<label className="p-col-12 label-left" htmlFor="control-view-area">
							<Translate {...this.props} data="view.extended.view.area" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-grid">
							<Dropdown inputId="control-view-area"
									  value={FunctionHelper.applyValue(
										  parameter.viewerPreferences?.viewArea, ViewerPreferences.getViewAreaDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.extended.area.media", {ns: "dialog"}
											  ),
											  value: PageBox.MediaBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.crop", {ns: "dialog"}
											  ),
											  value: PageBox.CropBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.bleed", {ns: "dialog"}
											  ),
											  value: PageBox.BleedBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.trim", {ns: "dialog"}
											  ),
											  value: PageBox.TrimBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.art", {ns: "dialog"}
											  ),
											  value: PageBox.ArtBox
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.PRINT_SCALE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.VIEW_CLIP)
					}>
						<label className="p-col-12 label-left" htmlFor="control-view-clip">
							<Translate {...this.props} data="view.extended.view.clip" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-grid">
							<Dropdown inputId="control-view-clip"
									  value={FunctionHelper.applyValue(
										  parameter.viewerPreferences?.viewClip, ViewerPreferences.getViewClipDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.extended.area.media", {ns: "dialog"}
											  ),
											  value: PageBox.MediaBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.crop", {ns: "dialog"}
											  ),
											  value: PageBox.CropBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.bleed", {ns: "dialog"}
											  ),
											  value: PageBox.BleedBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.trim", {ns: "dialog"}
											  ),
											  value: PageBox.TrimBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.art", {ns: "dialog"}
											  ),
											  value: PageBox.ArtBox
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.PRINT_SCALE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.PRINT_AREA)
					}>
						<label className="p-col-12 label-left" htmlFor="control-print-area">
							<Translate {...this.props} data="view.extended.print.area" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-grid">
							<Dropdown inputId="control-print-area"
									  value={FunctionHelper.applyValue(
										  parameter.viewerPreferences?.printArea,
										  ViewerPreferences.getPrintAreaDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.extended.area.media", {ns: "dialog"}
											  ),
											  value: PageBox.MediaBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.crop", {ns: "dialog"}
											  ),
											  value: PageBox.CropBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.bleed", {ns: "dialog"}
											  ),
											  value: PageBox.BleedBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.trim", {ns: "dialog"}
											  ),
											  value: PageBox.TrimBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.art", {ns: "dialog"}
											  ),
											  value: PageBox.ArtBox
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.PRINT_SCALE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ViewDialogFields.PRINT_CLIP)
					}>
						<label className="p-col-12 label-left" htmlFor="control-print-clip">
							<Translate {...this.props} data="view.extended.print.clip" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-grid">
							<Dropdown inputId="control-print-clip"
									  value={FunctionHelper.applyValue(
										  parameter.viewerPreferences?.printClip,
										  ViewerPreferences.getPrintClipDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "view.extended.area.media", {ns: "dialog"}
											  ),
											  value: PageBox.MediaBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.crop", {ns: "dialog"}
											  ),
											  value: PageBox.CropBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.bleed", {ns: "dialog"}
											  ),
											  value: PageBox.BleedBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.trim", {ns: "dialog"}
											  ),
											  value: PageBox.TrimBox
										  },
										  {
											  label: Translate.value(
												  "view.extended.area.art", {ns: "dialog"}
											  ),
											  value: PageBox.ArtBox
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ViewDialogFields.PRINT_SCALE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderContents(): ReactNode {
		let parameter: ToolboxOptionsOptions | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<TabView activeIndex={this.state.panel} onTabChange={
					(e) => this.setState({panel: e.index})
				}>
					<TabPanel header={Translate.value("view.tabs.general", {ns: "dialog"})}
							  headerClassName="control-tab-general" contentClassName="general-tab-panel">
						{this.renderGeneralTab(parameter!)}
					</TabPanel>
					<TabPanel header={Translate.value("view.tabs.print", {ns: "dialog"})}
							  headerClassName="control-tab-print" contentClassName="print-tab-panel">
						{this.renderPrintTab(parameter!)}
					</TabPanel>
					<TabPanel header={Translate.value("view.tabs.extended", {ns: "dialog"})}
							  headerClassName="control-tab-extended" contentClassName="extended-tab-panel">
						{this.renderExtendedTab(parameter!)}
					</TabPanel>
				</TabView>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["view-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.view.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.view.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: ViewDialogFields, value: any): void {
		let parameter: ToolboxOptionsOptions = this.state.parameter!.data;
		let errors: Map<ViewDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case ViewDialogFields.PAGE_MODE:
				parameter.pageLayout = value;
				break;
			case ViewDialogFields.OPEN_MODE:
				parameter.initialView = value;
				break;
			case ViewDialogFields.ZOOM_MODE:
				parameter.magnification = value;
				break;
			case ViewDialogFields.ZOOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxOptionsOptions.getMagnificationZoomDefault();
				}

				parameter.magnificationZoom = value;
				break;
			case ViewDialogFields.START_PAGE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxOptionsOptions.getInitialPageDefault();
				}

				parameter.initialPage = value;
				break;
			case ViewDialogFields.HIDE_MENUBAR:
				parameter.viewerPreferences!.hideMenuBar = value;
				break;
			case ViewDialogFields.HIDE_TOOLBAR:
				parameter.viewerPreferences!.hideToolbar = value;
				break;
			case ViewDialogFields.HIDE_WINDOW_UI:
				parameter.viewerPreferences!.hideWindowUI = value;
				break;
			case ViewDialogFields.CENTER_WINDOW:
				parameter.viewerPreferences!.centerWindow = value;
				break;
			case ViewDialogFields.FIT_WINDOW:
				parameter.viewerPreferences!.fitWindow = value;
				break;
			case ViewDialogFields.TEXT_DIRECTION:
				parameter.viewerPreferences!.textDirection = value;
				break;
			case ViewDialogFields.TITLE:
				parameter.viewerPreferences!.displayDocTitle = value;
				break;
			case ViewDialogFields.PRINT_SCALE:
				parameter.viewerPreferences!.printScaling = value;
				break;
			case ViewDialogFields.PRINT_COPIES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ViewerPreferences.getNumCopiesDefault();
				}

				parameter.viewerPreferences!.numCopies = value;
				break;
			case ViewDialogFields.PRINT_DUPLEX:
				parameter.viewerPreferences!.duplex = value;
				break;
			case ViewDialogFields.PRINT_TRAY:
				parameter.viewerPreferences!.printTraySelection = value;
				break;
			case ViewDialogFields.PRINT_PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					parameter.viewerPreferences!.printPageRange = ViewerPreferences.getPrintPageRangeDefault();
					errors.delete(field);
					break;
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.viewerPreferences!.printPageRange = value;
				break;
			case ViewDialogFields.VIEW_AREA:
				parameter.viewerPreferences!.viewArea = value;
				break;
			case ViewDialogFields.VIEW_CLIP:
				parameter.viewerPreferences!.viewClip = value;
				break;
			case ViewDialogFields.PRINT_AREA:
				parameter.viewerPreferences!.printArea = value;
				break;
			case ViewDialogFields.PRINT_CLIP:
				parameter.viewerPreferences!.printClip = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-annotation-star"}/>
					<Translate {...this.props} data="view.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxOptionsOptions | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let pageLayout: MetadataPageLayout | undefined = portalDocument.metadata?.information?.pageLayout;
		switch (pageLayout) {
			case MetadataPageLayout.OneColumn:
				parameter!.pageLayout = PageLayout.SinglePageContinous;
				break;
			case MetadataPageLayout.TwoColumnLeft:
				parameter!.pageLayout = PageLayout.TwoPagesContinous;
				break;
			case MetadataPageLayout.TwoColumnRight:
				parameter!.pageLayout = PageLayout.TwoPagesContinousRight;
				break;
			case MetadataPageLayout.TwoPageLeft:
				parameter!.pageLayout = PageLayout.TwoPages;
				break;
			case MetadataPageLayout.TwoPageRight:
				parameter!.pageLayout = PageLayout.TwoPagesRight;
				break;
			case MetadataPageLayout.SinglePage:
			default:
				parameter!.pageLayout = PageLayout.SinglePage;
				break;
		}

		let initialView: MetadataInitialView | undefined = portalDocument.metadata?.information?.initialView;
		switch (initialView) {
			case MetadataInitialView.Outlines:
				parameter!.initialView = InitialView.Outlines;
				break;
			case MetadataInitialView.Thumbnails:
				parameter!.initialView = InitialView.Thumbnails;
				break;
			case MetadataInitialView.Fullscreen:
				parameter!.initialView = InitialView.Fullscreen;
				break;
			case MetadataInitialView.Attachments:
				parameter!.initialView = InitialView.Attachments;
				break;
			case MetadataInitialView.Layers:
				parameter!.initialView = InitialView.Layers;
				break;
			case MetadataInitialView.None:
			default:
				parameter!.initialView = InitialView.None;
				break;
		}

		let magnification: MetadataMagnification | undefined = portalDocument.metadata?.information?.magnification;
		switch (magnification) {
			case MetadataMagnification.FitWidth:
				parameter!.magnification = Magnification.FitWidth;
				break;
			case MetadataMagnification.FitVisible:
				parameter!.magnification = Magnification.FitVisible;
				break;
			case MetadataMagnification.FitHeight:
				parameter!.magnification = Magnification.FitHeight;
				break;
			case MetadataMagnification.FitPage:
				parameter!.magnification = Magnification.FitPage;
				break;
			case MetadataMagnification.FitActual:
				parameter!.magnification = Magnification.FitActual;
				break;
			case MetadataMagnification.Zoom:
				parameter!.magnification = Magnification.Zoom;
				break;
			case MetadataMagnification.None:
			default:
				parameter!.magnification = Magnification.None;
				break;
		}

		let initialPage: number | undefined = portalDocument.metadata?.information?.initialPage;
		if (FunctionHelper.exists(initialPage)) {
			parameter!.initialPage = initialPage;
		}

		let magnificationZoom: number | undefined = portalDocument.metadata?.information?.magnificationZoom;
		if (FunctionHelper.exists(magnificationZoom)) {
			parameter!.magnificationZoom = magnificationZoom;
		}

		let hideToolbar: boolean | undefined = portalDocument.metadata?.information?.viewerPreferences?.hideToolbar;
		if (FunctionHelper.exists(hideToolbar)) {
			parameter!.viewerPreferences!.hideToolbar = hideToolbar;
		}

		let hideMenuBar: boolean | undefined = portalDocument.metadata?.information?.viewerPreferences?.hideMenuBar;
		if (FunctionHelper.exists(hideMenuBar)) {
			parameter!.viewerPreferences!.hideMenuBar = hideMenuBar;
		}

		let hideWindowUI: boolean | undefined = portalDocument.metadata?.information?.viewerPreferences?.hideWindowUI;
		if (FunctionHelper.exists(hideWindowUI)) {
			parameter!.viewerPreferences!.hideWindowUI = hideWindowUI;
		}

		let fitWindow: boolean | undefined = portalDocument.metadata?.information?.viewerPreferences?.fitWindow;
		if (FunctionHelper.exists(fitWindow)) {
			parameter!.viewerPreferences!.fitWindow = fitWindow;
		}

		let centerWindow: boolean | undefined = portalDocument.metadata?.information?.viewerPreferences?.centerWindow;
		if (FunctionHelper.exists(centerWindow)) {
			parameter!.viewerPreferences!.centerWindow = centerWindow;
		}

		let displayDocTitle: boolean | undefined = portalDocument.metadata?.information?.viewerPreferences?.displayDocTitle;
		if (FunctionHelper.exists(displayDocTitle)) {
			parameter!.viewerPreferences!.displayDocTitle = displayDocTitle;
		}

		let nonFullScreenPageMode: MetadataNonFullScreenPageMode | undefined =
			portalDocument.metadata?.information?.viewerPreferences?.nonFullScreenPageMode;
		switch (nonFullScreenPageMode) {
			case MetadataNonFullScreenPageMode.None:
				parameter!.viewerPreferences!.nonFullScreenPageMode = NonFullScreenPageMode.None
				break;
			case MetadataNonFullScreenPageMode.Outlines:
				parameter!.viewerPreferences!.nonFullScreenPageMode = NonFullScreenPageMode.Outlines
				break;
			case MetadataNonFullScreenPageMode.Thumbnails:
				parameter!.viewerPreferences!.nonFullScreenPageMode = NonFullScreenPageMode.Thumbnails
				break;
			case MetadataNonFullScreenPageMode.OptionalContents:
				parameter!.viewerPreferences!.nonFullScreenPageMode = NonFullScreenPageMode.OptionalContents
				break;
		}

		let textDirection: MetadataTextDirection | undefined =
			portalDocument.metadata?.information?.viewerPreferences?.textDirection;
		switch (textDirection) {
			case MetadataTextDirection.LeftToRight:
				parameter!.viewerPreferences!.textDirection = TextDirection.LeftToRight;
				break;
			case MetadataTextDirection.RightToLeft:
				parameter!.viewerPreferences!.textDirection = TextDirection.RightToLeft;
				break;
		}

		let viewArea: BoxSelection | undefined = portalDocument.metadata?.information?.viewerPreferences?.viewArea;
		switch (viewArea) {
			case BoxSelection.ArtBox:
				parameter!.viewerPreferences!.viewArea = PageBox.ArtBox;
				break;
			case BoxSelection.BleedBox:
				parameter!.viewerPreferences!.viewArea = PageBox.BleedBox;
				break;
			case BoxSelection.TrimBox:
				parameter!.viewerPreferences!.viewArea = PageBox.TrimBox;
				break;
			case BoxSelection.CropBox:
				parameter!.viewerPreferences!.viewArea = PageBox.CropBox;
				break;
			case BoxSelection.MediaBox:
				parameter!.viewerPreferences!.viewArea = PageBox.MediaBox;
				break;
		}

		let viewClip: BoxSelection | undefined = portalDocument.metadata?.information?.viewerPreferences?.viewClip;
		switch (viewClip) {
			case BoxSelection.ArtBox:
				parameter!.viewerPreferences!.viewClip = PageBox.ArtBox;
				break;
			case BoxSelection.BleedBox:
				parameter!.viewerPreferences!.viewClip = PageBox.BleedBox;
				break;
			case BoxSelection.TrimBox:
				parameter!.viewerPreferences!.viewClip = PageBox.TrimBox;
				break;
			case BoxSelection.CropBox:
				parameter!.viewerPreferences!.viewClip = PageBox.CropBox;
				break;
			case BoxSelection.MediaBox:
				parameter!.viewerPreferences!.viewClip = PageBox.MediaBox;
				break;
		}

		let printArea: BoxSelection | undefined = portalDocument.metadata?.information?.viewerPreferences?.printArea;
		switch (printArea) {
			case BoxSelection.ArtBox:
				parameter!.viewerPreferences!.printArea = PageBox.ArtBox;
				break;
			case BoxSelection.BleedBox:
				parameter!.viewerPreferences!.printArea = PageBox.BleedBox;
				break;
			case BoxSelection.TrimBox:
				parameter!.viewerPreferences!.printArea = PageBox.TrimBox;
				break;
			case BoxSelection.CropBox:
				parameter!.viewerPreferences!.printArea = PageBox.CropBox;
				break;
			case BoxSelection.MediaBox:
				parameter!.viewerPreferences!.printArea = PageBox.MediaBox;
				break;
		}

		let printClip: BoxSelection | undefined = portalDocument.metadata?.information?.viewerPreferences?.printClip;
		switch (printClip) {
			case BoxSelection.ArtBox:
				parameter!.viewerPreferences!.printClip = PageBox.ArtBox;
				break;
			case BoxSelection.BleedBox:
				parameter!.viewerPreferences!.printClip = PageBox.BleedBox;
				break;
			case BoxSelection.TrimBox:
				parameter!.viewerPreferences!.printClip = PageBox.TrimBox;
				break;
			case BoxSelection.CropBox:
				parameter!.viewerPreferences!.printClip = PageBox.CropBox;
				break;
			case BoxSelection.MediaBox:
				parameter!.viewerPreferences!.printClip = PageBox.MediaBox;
				break;
		}

		let printScaling: MetadataPrintScale | undefined =
			portalDocument.metadata?.information?.viewerPreferences?.printScaling;
		switch (printScaling) {
			case MetadataPrintScale.ReaderDefault:
				parameter!.viewerPreferences!.printScaling = PrintScale.ReaderDefault;
				break;
			case MetadataPrintScale.NoPrintScaling:
				parameter!.viewerPreferences!.printScaling = PrintScale.NoPrintScaling;
				break;
		}

		let duplex: MetadataDuplex | undefined = portalDocument.metadata?.information?.viewerPreferences?.duplex;
		switch (duplex) {
			case MetadataDuplex.None:
				parameter!.viewerPreferences!.duplex = Duplex.None;
				break;
			case MetadataDuplex.Simplex:
				parameter!.viewerPreferences!.duplex = Duplex.Simplex;
				break;
			case MetadataDuplex.DuplexFlipShortEdge:
				parameter!.viewerPreferences!.duplex = Duplex.DuplexFlipShortEdge;
				break;
			case MetadataDuplex.DuplexFlipLongEdge:
				parameter!.viewerPreferences!.duplex = Duplex.DuplexFlipLongEdge;
				break;
		}

		let printTraySelection: MetadataPrintTraySelection | undefined =
			portalDocument.metadata?.information?.viewerPreferences?.printTraySelection;
		switch (printTraySelection) {
			case MetadataPrintTraySelection.ReaderDefault:
				parameter!.viewerPreferences!.printTraySelection = PrintTraySelection.ReaderDefault;
				break;
			case MetadataPrintTraySelection.ByPDFSize:
				parameter!.viewerPreferences!.printTraySelection = PrintTraySelection.ByPDFSize;
				break;
		}

		let printPageRange: string | undefined = portalDocument.metadata?.information?.viewerPreferences?.printPageRange;
		if (FunctionHelper.exists(printPageRange)) {
			parameter!.viewerPreferences!.printPageRange = printPageRange;
		}

		let numCopies: number | undefined = portalDocument.metadata?.information?.viewerPreferences?.numCopies;
		if (FunctionHelper.exists(numCopies)) {
			parameter!.viewerPreferences!.numCopies = numCopies;
		}

		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		this.setState({
			panel: 0,
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			options: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.view", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.VIEW;
	}

	protected getDefaultParameter(): Parameter<ToolboxOptionsOptions> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			panel: 0
		});
	}
}

const ViewDialog = React.memo(ViewDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxOptionsOptions>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxOptionsOptions>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default ViewDialog;